import { useFrontendConfigContext } from '@apeiron/config'
import { ContractWorkStatus } from '@apeiron/contract'
import { CurrencyTokenType, useCurrencyContext } from '@apeiron/currency'
import {
  ApeironBlueButton,
  INFO_POPUP_TYPE,
  InfoPopup,
  useAccountContext
} from '@apeiron/temp'
import { ApeironCheckbox } from '@apeiron/ui'
import { Box, styled, Typography } from '@mui/material'
import OrbitalTrackSelectPlanet from '@src/components/orbitalTrack/attach/OrbitalTrackSelectPlanet'
import OrbitalTrackOperatingStarInfo from '@src/components/orbitalTrack/detachRenew/OrbitalTrackOperatingStarInfo'
import ApeironScrollBox from '@src/components/share/apeiron/ApeironScrollBox'
import { useOrbitalTrackAttachContext } from '@src/contexts/orbitalTrack/OrbitalTrackAttachContext'
import useOrbitalTrackAttach from '@src/hooks/contract/useOrbitalTrackAttach'
import * as R from 'ramda'
import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const ModalBox = styled(InfoPopup)`
  max-height: 95vh;
  max-width: 1020px;
  width: 95vw;
`

const StyledApeironScrollBox = styled(ApeironScrollBox)`
  max-height: calc(95vh - 50px);
  overflow-y: auto;
  position: relative;
`

const LowerContainer = styled(Box)`
  align-items: center;
  background-color: #171719;
  display: flex;
  flex-direction: column;
  gap: 30px;
  width: 100%;
  padding: 30px 0px;
`

const CheckoutContainer = styled(Box)`
  align-items: center;
  border-bottom: 2px dashed #4d4c4c;
  border-top: 2px dashed #4d4c4c;
  display: flex;
  justify-content: space-between;
  padding: 25px 50px;
  width: 100%;
  ${props => props.theme.breakpoints.down('res480')} {
    flex-direction: column;
  }
`

const CheckoutTitle = styled(Typography)`
  color: white;
  font-size: 18px;
  font-weight: 300;
`

const CheckoutFee = styled(Typography)`
  color: #ffbf36;
  font-size: 36px;
  font-weight: 700;
`

const CheckoutUnit = styled(Typography)`
  color: #80807e;
  display: inline;
  font-size: 16px;
  font-weight: 300;
  margin-left: 5px;
`

const NotEnoughFee = styled(Typography)`
  color: #ff0000;
  font-size: 16px;
  font-weight: 300;
  text-align: right;
`

const AgreementCheckbox = styled(ApeironCheckbox)`
  ${props => props.theme.breakpoints.down('res1280')} {
    max-width: 625px;
    width: 90%;
    .Custom-Description {
      max-width: 600px;
    }
  }
`

const ConfirmButton = styled(ApeironBlueButton)`
  font-size: 16px;
  ${props => props.theme.breakpoints.up('res480')} {
    padding: 0 60px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 90%;
  }
`

const Content: FC<ContentProps> = props => {
  const { isOwner = false, onClose } = props

  const { config } = useFrontendConfigContext()

  const { account } = useAccountContext()

  const { getApeironPrice, getCurrency } = useCurrencyContext()

  const {
    done,
    filledPlanets,
    loading,
    setting,
    star,
    setAndFillSelectedPlanets,
    setDone
  } = useOrbitalTrackAttachContext()

  const { attachPlanets } = useOrbitalTrackAttach(setting?.currencyType)

  const { t } = useTranslation()

  const [checked, setChecked] = useState<boolean>(false)

  const totalFee = useMemo(() => {
    if (!R.isNil(setting)) {
      const amount = String(Number(setting.rentalFee) * R.length(filledPlanets))

      return getApeironPrice(setting.currencyType, amount)
    }

    return '0'
  }, [filledPlanets, setting, getApeironPrice])

  const enoughFee = useMemo((): boolean => {
    if (!R.isNil(setting)) {
      const balance = BigInt(
        R.pathOr('0', ['balances', CurrencyTokenType.Usdc, 'amount'], account)
      )

      const amount = BigInt(setting.rentalFee) * BigInt(R.length(filledPlanets))

      return balance >= amount
    }

    return true
  }, [account, filledPlanets, setting])

  const handleOnCheckBoxChange = useCallback(() => {
    setChecked(!checked)
  }, [checked])

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose(done)
    }
  }, [done, onClose])

  const handleOnConfirm = useCallback(async () => {
    if (!R.isNil(star) && !R.isEmpty(filledPlanets)) {
      const attachedIds = await attachPlanets(star, filledPlanets, setting)

      if (!R.isEmpty(attachedIds)) {
        const successPlanets = R.map(planet => {
          const status = R.includes(planet.planetID, attachedIds)
            ? ContractWorkStatus.SUCCESS
            : ContractWorkStatus.FAIL

          return R.assoc('workStatus', status, planet)
        }, filledPlanets)

        setAndFillSelectedPlanets(successPlanets)
        setDone(true)
      }
    }
  }, [
    star,
    filledPlanets,
    setting,
    attachPlanets,
    setAndFillSelectedPlanets,
    setDone
  ])

  const cooldown = config?.orbitRental.cooldown || 0

  const confirmText = isOwner
    ? t('common.confirm')
    : t('godiverse.orbital_track.rent')

  const buttonText = done ? t('common.button.done') : confirmText

  const currency = getCurrency(setting?.currencyType)

  return (
    <StyledApeironScrollBox>
      <OrbitalTrackOperatingStarInfo
        isOwner={isOwner}
        loading={loading}
        setting={setting}
        star={star}
        subtitle={t('godiverse.orbital_track.attach_planet.rental_details')}
        title={t('godiverse.orbital_track.attach_planet.title')}
      />
      <LowerContainer>
        <OrbitalTrackSelectPlanet isOwner={isOwner} />
        {!isOwner && (
          <CheckoutContainer>
            <CheckoutTitle>
              {t('godiverse.orbital_track.total_fee')}
            </CheckoutTitle>
            {enoughFee ? (
              <CheckoutFee>
                {totalFee}
                <CheckoutUnit>{currency?.name || ''}</CheckoutUnit>
              </CheckoutFee>
            ) : (
              <NotEnoughFee>{t('common.no_balance')}</NotEnoughFee>
            )}
          </CheckoutContainer>
        )}
        <AgreementCheckbox
          checked={checked}
          description={
            isOwner
              ? t('godiverse.orbital_track.attach_planet.checkbox_by_owner', {
                  count: cooldown
                })
              : t('godiverse.orbital_track.attach_planet.checkbox', {
                  count: cooldown
                })
          }
          onChange={handleOnCheckBoxChange}
        />
        <ConfirmButton
          disabled={R.isEmpty(filledPlanets) || !checked || !enoughFee}
          onClick={done ? handleOnClose : handleOnConfirm}
        >
          {buttonText}
        </ConfirmButton>
      </LowerContainer>
    </StyledApeironScrollBox>
  )
}

const OrbitalTrackAttachDialog: FC<Props> = props => {
  const { className, isOpened, onClose, ...rest } = props

  const handleOnClose = useCallback(() => {
    if (onClose) {
      onClose(false)
    }
  }, [onClose])

  return (
    <ModalBox
      className={className}
      type={INFO_POPUP_TYPE.BLACK}
      keepMounted={false}
      open={isOpened}
      onClose={handleOnClose}
    >
      <Content {...rest} onClose={onClose} />
    </ModalBox>
  )
}

type ContentProps = {
  isOwner?: boolean
  onClose?: (success: boolean) => void
}

type Props = {
  className?: string
  isOpened: boolean
} & ContentProps

export default OrbitalTrackAttachDialog
